import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { JobPost } from "@/data/jobPosts";
import { useLanguage } from "@/hooks/useLanguage";

interface JobDetailContentProps {
  job: JobPost;
}

const JobDetailContent = ({ job }: JobDetailContentProps) => {
  const { t, localizePath } = useLanguage();

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-10">
              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">{t("careers.jobDetail.aboutRole")}</h2>
                <div 
                  className="prose prose-lg max-w-none text-muted-foreground [&>p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: job.description }}
                />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">{t("careers.jobDetail.tasks")}</h2>
                <ul className="space-y-3">
                  {job.responsibilities.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">{t("careers.jobDetail.requirements")}</h2>
                <ul className="space-y-3">
                  {job.requirements.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {job.niceToHave.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-primary mb-4">{t("careers.jobDetail.niceToHave")}</h2>
                  <ul className="space-y-3">
                    {job.niceToHave.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 border-2 border-muted-foreground/30 rounded-full shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">{t("careers.jobDetail.benefits")}</h2>
                <ul className="space-y-3">
                  {job.benefits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 bg-secondary/30 rounded-2xl p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t("careers.jobDetail.interested")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t("careers.jobDetail.interestedText")}
                  </p>
                </div>

                <Button 
                  size="lg" 
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <a href={`mailto:jobs@chathero.ai?subject=Bewerbung: ${job.title}`}>
                    {t("careers.jobDetail.applyNow")}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>

                <div className="text-sm text-muted-foreground">
                  <p className="mb-2">{t("careers.jobDetail.sendTo")}</p>
                  <a 
                    href="mailto:jobs@chathero.ai" 
                    className="text-accent hover:underline font-medium"
                  >
                    jobs@chathero.ai
                  </a>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">{t("careers.jobDetail.questions")}</p>
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <Link to={localizePath("/kontakt")}>
                      {t("careers.jobDetail.scheduleChat")}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobDetailContent;
